/**
 * What each SOURCE holds for this product, beside what we hold ourselves.
 *
 * The product page used to show our photographs and nothing else, so the question
 * an operator actually has — does the customer see a picture, whose is it, and is
 * there a datasheet — could only be answered by opening the B2B and looking. Every
 * panel here is read-only on purpose: these are the source's own assets, rendered
 * verbatim, and the only thing we can honestly do with them is show them.
 *
 * Grouped by origin rather than merged into one gallery, because provenance is
 * the point. "TecDoc has five photos and two documents, Kromeda has nothing, we
 * have two" is the answer; a single grid with badges makes it something you have
 * to reconstruct by counting.
 */
import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../lib/api';
import type { ProductSources, SourceImage, SourcePanel, SourcePdf } from '../lib/types';
import { Chip, Empty, ErrorBox, Loading, SourceChip } from '../ui';

/**
 * TecDoc publishes one URL per rendered size and we want a thumbnail — 400 is the
 * one every article has. The fallbacks are for an entry that carries a different
 * set, which costs nothing to tolerate and saves an empty tile.
 */
function thumb(img: SourceImage): string | null {
  for (const k of ['imageURL400', 'imageURL200', 'imageURL800', 'imageURL100', 'imageURL1600']) {
    const v = img[k];
    if (typeof v === 'string' && v) return v;
  }
  return null;
}

function textOf(blocks: unknown[]): string[] {
  const out: string[] = [];
  for (const b of blocks) {
    const t = (b as { text?: unknown })?.text;
    if (Array.isArray(t)) out.push(...t.map(String));
    else if (typeof t === 'string') out.push(t);
  }
  return out;
}

function criterionOf(c: unknown): { label: string; value: string } | null {
  const o = c as Record<string, unknown> | null;
  if (!o) return null;
  const label = String(o.criteriaDescription ?? o.criteriaName ?? '').trim();
  const value = [o.rawValue ?? o.formattedValue ?? o.criteriaValue ?? '', o.criteriaUnitDescription ?? '']
    .map((v) => String(v ?? '').trim())
    .filter(Boolean)
    .join(' ');
  return label ? { label, value } : null;
}

function Documents({ pdfs }: { pdfs: SourcePdf[] }) {
  if (pdfs.length === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-1">
      {pdfs.map((p, i) => (
        <div key={`${p.url}-${i}`} className="flex flex-wrap items-center gap-2 text-[12px]">
          <span className="chip border-line">PDF</span>
          <a href={p.url} target="_blank" rel="noreferrer" className="mono truncate" style={{ maxWidth: 320 }}>
            {p.fileName ?? p.url}
          </a>
          {p.headerDescription && <span className="text-faint">{p.headerDescription}</span>}
        </div>
      ))}
    </div>
  );
}

function Images({ images }: { images: SourceImage[] }) {
  if (images.length === 0) return null;
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
      {images.map((img, i) => {
        const url = thumb(img);
        return (
          <figure key={i} className="flex flex-col gap-1 rounded border border-line bg-panel2 p-1.5">
            {url ? (
              <a href={url} target="_blank" rel="noreferrer">
                <img
                  src={url}
                  alt={img.fileName ?? ''}
                  loading="lazy"
                  className="h-24 w-full rounded bg-panel object-contain"
                />
              </a>
            ) : (
              <div className="flex h-24 w-full items-center justify-center rounded bg-panel text-[11px] text-faint">
                no renderable size
              </div>
            )}
            <figcaption className="mono truncate text-[10.5px] text-faint" title={img.fileName ?? ''}>
              {img.fileName ?? `#${i + 1}`}
            </figcaption>
            {img.headerDescription && (
              <span className="truncate text-[10.5px] text-faint">{img.headerDescription}</span>
            )}
          </figure>
        );
      })}
    </div>
  );
}

function Details({ detail }: { detail: NonNullable<SourcePanel['detail']> }) {
  const [open, setOpen] = useState(false);
  const criteria = detail.articleCriteria.map(criterionOf).filter((c): c is { label: string; value: string } => !!c);
  const text = textOf(detail.articleText);
  const gtins = detail.gtins.map(String).filter(Boolean);
  const trades = detail.tradeNumbers.map(String).filter(Boolean);

  return (
    <div className="mt-2">
      <button className="btn btn-sm" onClick={() => setOpen(!open)}>
        {open ? 'Hide' : 'Show'} details ({criteria.length} specs · {gtins.length} GTIN · {detail.oemNumbers.length} OE ·{' '}
        {trades.length} trade)
      </button>
      {open && (
        <div className="mt-2 flex flex-col gap-2 rounded border border-line bg-panel2 p-2 text-[12px]">
          {text.length > 0 && (
            <div>
              <div className="text-[11px] text-faint">Article text</div>
              {text.map((t, i) => (
                <div key={i}>{t}</div>
              ))}
            </div>
          )}
          {criteria.length > 0 && (
            <div>
              <div className="text-[11px] text-faint">Specifications</div>
              <div className="grid gap-x-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
                {criteria.map((c, i) => (
                  <div key={i} className="flex justify-between gap-2 border-b border-line py-0.5">
                    <span className="truncate text-faint" title={c.label}>
                      {c.label}
                    </span>
                    <span className="mono shrink-0">{c.value || '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {gtins.length > 0 && (
            <div>
              <div className="text-[11px] text-faint">GTIN / EAN</div>
              <div className="mono">{gtins.join(', ')}</div>
            </div>
          )}
          {trades.length > 0 && (
            <div>
              <div className="text-[11px] text-faint">Trade numbers</div>
              <div className="mono break-all">{trades.join(', ')}</div>
            </div>
          )}
          {detail.misc && (
            <div>
              <div className="text-[11px] text-faint">Lifecycle</div>
              <div className="mono break-all">{JSON.stringify(detail.misc)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Panel({ panel }: { panel: SourcePanel }) {
  const d = panel.detail;
  const head = [
    panel.supplierId ? `supplier ${panel.supplierId}` : null,
    panel.code ? `art ${panel.code}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="rounded border border-line p-2.5">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <SourceChip source={panel.source} />
        {panel.brandName && <span className="text-[12px]">{panel.brandName}</span>}
        <span className="mono text-[11.5px] text-faint">{head}</span>
        {d && (
          <span className="text-[11.5px] text-faint">
            ({d.images.length} image{d.images.length === 1 ? '' : 's'}, {d.pdfs.length} document
            {d.pdfs.length === 1 ? '' : 's'})
          </span>
        )}
        {panel.from === 'live' && (
          <Chip className="border-ok text-ok" >fetched now</Chip>
        )}
        {panel.via && (
          <span className="ml-auto text-[11px] text-faint" title="which spelling we asked under">
            asked via {panel.via}
          </span>
        )}
      </div>

      {!d ? (
        <div className="text-[12px] text-faint">{panel.unavailable ?? 'nothing held'}</div>
      ) : (
        <>
          {d.images.length === 0 && d.pdfs.length === 0 && (
            <div className="text-[12px] text-faint">an article, but no images and no documents</div>
          )}
          <Images images={d.images} />
          <Documents pdfs={d.pdfs} />
          <Details detail={d} />
        </>
      )}
    </div>
  );
}

export default function SourceAssets({ productId }: { productId: number }) {
  const { request } = useApi();
  const [data, setData] = useState<ProductSources | null>(null);
  const [error, setError] = useState('');
  const [live, setLive] = useState(true);

  const load = useCallback(async () => {
    setData(null);
    const r = await request<ProductSources>('GET', `/v1/products/${productId}/sources?live=${live ? '1' : '0'}`);
    if (!r.ok) {
      setError(typeof r.data === 'object' && r.data && 'error' in r.data ? String(r.data.error) : `HTTP ${r.status}`);
      return;
    }
    setError('');
    setData(r.data);
  }, [productId, request, live]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorBox>{error}</ErrorBox>;
  if (!data) return <Loading label={live ? 'Asking every source…' : 'Reading the cache…'} />;

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] text-faint">
          read-only — each catalogue&rsquo;s own assets, exactly as it publishes them
        </span>
        <button className="btn btn-sm ml-auto" onClick={() => setLive(!live)}>
          {live ? 'Cache only' : 'Ask the sources'}
        </button>
      </div>
      {data.sources.length === 0 ? (
        <Empty>No source knows this product under any code we hold.</Empty>
      ) : (
        data.sources.map((p) => <Panel key={`${p.source}-${p.supplierId ?? ''}`} panel={p} />)
      )}
    </div>
  );
}
